import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

interface MathTextProps {
  text: string;
  format?: string;
}

/**
 * Renders text with KaTeX support.
 * - format='tex': entire content is LaTeX math (rendered as InlineMath per line)
 * - format='txt' (default): $...$ → inline math, $$...$$ → block math, rest → \text{}
 * Newlines produce actual line breaks.
 */
export default function MathText({ text, format }: MathTextProps) {
  if (!text) return <>{text}</>;

  // If format is 'tex' but content contains '$', it's actually mixed text+math
  const effectiveFormat = (format === 'tex' && text.includes('$')) ? 'txt' : format;

  const lines = text.split('\n');

  return (
    <span>
      {lines.map((line, lineIdx) => (
        <span key={lineIdx}>
          {lineIdx > 0 && <br />}
          {effectiveFormat === 'tex' ? (
            line ? <InlineMath math={line} /> : null
          ) : (
            <LineParts line={line} />
          )}
        </span>
      ))}
    </span>
  );
}

function LineParts({ line }: { line: string }) {
  if (!line) return null;

  if (!line.includes('$')) {
    return <InlineMath math={'\\text{' + escapeTex(line) + '}'} />;
  }

  const parts: { type: 'text' | 'inline' | 'block'; content: string }[] = [];
  let remaining = line;

  while (remaining.length > 0) {
    const blockStart = remaining.indexOf('$$');
    const inlineStart = remaining.indexOf('$');

    if (blockStart !== -1 && blockStart === inlineStart) {
      if (blockStart > 0) {
        parts.push({ type: 'text', content: remaining.slice(0, blockStart) });
      }
      const blockEnd = remaining.indexOf('$$', blockStart + 2);
      if (blockEnd !== -1) {
        parts.push({ type: 'block', content: remaining.slice(blockStart + 2, blockEnd) });
        remaining = remaining.slice(blockEnd + 2);
      } else {
        parts.push({ type: 'text', content: remaining });
        remaining = '';
      }
    } else if (inlineStart !== -1) {
      if (inlineStart > 0) {
        parts.push({ type: 'text', content: remaining.slice(0, inlineStart) });
      }
      const inlineEnd = remaining.indexOf('$', inlineStart + 1);
      if (inlineEnd !== -1) {
        parts.push({ type: 'inline', content: remaining.slice(inlineStart + 1, inlineEnd) });
        remaining = remaining.slice(inlineEnd + 1);
      } else {
        parts.push({ type: 'text', content: remaining });
        remaining = '';
      }
    } else {
      parts.push({ type: 'text', content: remaining });
      remaining = '';
    }
  }

  return (
    <>
      {parts.map((part, i) => {
        if (part.type === 'block') {
          return <BlockMath key={i} math={part.content} />;
        }
        if (part.type === 'inline') {
          return <InlineMath key={i} math={part.content} />;
        }
        return <InlineMath key={i} math={'\\text{' + escapeTex(part.content) + '}'} />;
      })}
    </>
  );
}

function escapeTex(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/[{}]/g, '\\$&');
}
